/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ExternalLink, Copy, CheckCircle2, ChevronDown, ChevronRight,
  Key, Globe, Webhook, Shield, Settings, Facebook, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CopyBlock({ label, value }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`Copied ${label}`);
  };
  return (
    <div className="flex items-center gap-2 bg-slate-50 border rounded-lg px-3 py-2 text-sm font-mono">
      <span className="truncate flex-1 text-slate-700">{value}</span>
      <button onClick={handleCopy} className="shrink-0 text-slate-400 hover:text-blue-600 transition-colors" data-testid={`copy-${label}`}>
        <Copy size={14} />
      </button>
    </div>
  );
}

function Step({ number, title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
        data-testid={`step-${number}-toggle`}
      >
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">
          {number}
        </div>
        <Icon size={18} className="text-slate-500 shrink-0" />
        <span className="font-semibold text-slate-900 flex-1">{title}</span>
        {open ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
      </button>
      {open && <div className="px-5 pb-5 pt-0 border-t">{children}</div>}
    </div>
  );
}

export default function MetaAdsSetupGuide({ onBack }) {
  const webhookUrl = `${API_URL}/api/meta-ads/webhook/receive`;
  const webhookVerifyUrl = `${API_URL}/api/meta-ads/webhook/verify`;
  const callbackUrl = `${API_URL}/api/meta-ads/callback`;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="meta-setup-guide">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-500 hover:text-blue-600 transition-colors" data-testid="guide-back-btn">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Meta Ads Setup Guide</h2>
          <p className="text-sm text-slate-500">Follow these steps to connect your Facebook/Instagram ad leads</p>
        </div>
      </div>

      {/* Pre-requisites */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-amber-800">
          <strong>Before you start:</strong> You need a Facebook Business account and admin access to the Facebook Page running your ads.
          If you don't have one, create it at{' '}
          <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="underline font-medium">
            business.facebook.com
          </a>.
        </div>
      </div>

      {/* Step 1 */}
      <Step number={1} title="Create a Meta Developer App" icon={Facebook} defaultOpen>
        <div className="space-y-3 text-sm text-slate-600 mt-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>
              Go to{' '}
              <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-blue-600 underline font-medium">
                developers.facebook.com/apps
              </a>{' '}
              and click <strong>"Create App"</strong>.
            </li>
            <li>Select <strong>"Business"</strong> as the app type. Click <strong>Next</strong>.</li>
            <li>Enter an app name (e.g., <strong>"Dnotours Lead Sync"</strong>), set your Business Portfolio, and click <strong>Create App</strong>.</li>
            <li>
              On the <strong>Add Products</strong> screen, find <strong>"Facebook Login"</strong> and click <strong>Set Up</strong>.
              Also find <strong>"Webhooks"</strong> and click <strong>Set Up</strong>.
            </li>
          </ol>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-blue-800">
            <strong>Tip:</strong> Your new app will be in <em>Development</em> mode. You can test with admin accounts. To go live, submit the app for App Review (Step 5).
          </div>
        </div>
      </Step>

      {/* Step 2 */}
      <Step number={2} title="Get App ID & App Secret" icon={Key}>
        <div className="space-y-3 text-sm text-slate-600 mt-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>In your app dashboard, go to <strong>Settings &rarr; Basic</strong>.</li>
            <li>Copy the <strong>App ID</strong> and <strong>App Secret</strong> (click "Show" to reveal it).</li>
            <li>
              Add these to your server's environment variables:
            </li>
          </ol>
          <div className="bg-slate-900 rounded-lg p-4 text-green-400 font-mono text-xs leading-relaxed">
            <div>META_APP_ID=<span className="text-yellow-300">your_app_id_here</span></div>
            <div>META_APP_SECRET=<span className="text-yellow-300">your_app_secret_here</span></div>
          </div>
          <p className="text-slate-500 text-xs">
            These go in your backend <code className="bg-slate-100 px-1 rounded">.env</code> file on the production server.
          </p>
        </div>
      </Step>

      {/* Step 3 */}
      <Step number={3} title="Configure Facebook Login (OAuth)" icon={Globe}>
        <div className="space-y-3 text-sm text-slate-600 mt-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>In your app dashboard, click <strong>Facebook Login &rarr; Settings</strong> (left sidebar).</li>
            <li>
              In <strong>"Valid OAuth Redirect URIs"</strong>, add:
            </li>
          </ol>
          <CopyBlock label="callback-url" value={callbackUrl} />
          <ol className="list-decimal list-inside space-y-2" start={3}>
            <li>Click <strong>Save Changes</strong>.</li>
            <li>
              Also add this callback URL to your backend environment:
            </li>
          </ol>
          <div className="bg-slate-900 rounded-lg p-4 text-green-400 font-mono text-xs">
            META_CALLBACK_URL=<span className="text-yellow-300">{callbackUrl}</span>
          </div>
        </div>
      </Step>

      {/* Step 4 */}
      <Step number={4} title="Set Up Webhooks (Real-Time Leads)" icon={Webhook}>
        <div className="space-y-3 text-sm text-slate-600 mt-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>In your app dashboard, go to <strong>Webhooks</strong> (left sidebar) or <strong>Products &rarr; Webhooks</strong>.</li>
            <li>Select <strong>"Page"</strong> from the dropdown, then click <strong>Subscribe to this topic</strong>.</li>
            <li>
              Enter the following <strong>Callback URL</strong>:
            </li>
          </ol>
          <CopyBlock label="webhook-url" value={webhookUrl} />
          <ol className="list-decimal list-inside space-y-2" start={4}>
            <li>
              Enter a <strong>Verify Token</strong> — choose any secret string (e.g., <code className="bg-slate-100 px-1 rounded">dnotours_verify_2024</code>).
            </li>
            <li>
              Add this same token to your backend environment:
            </li>
          </ol>
          <div className="bg-slate-900 rounded-lg p-4 text-green-400 font-mono text-xs">
            META_WEBHOOK_VERIFY_TOKEN=<span className="text-yellow-300">dnotours_verify_2024</span>
          </div>
          <ol className="list-decimal list-inside space-y-2" start={6}>
            <li>Click <strong>Verify and Save</strong>. Meta will send a GET request to your URL to confirm.</li>
            <li>
              After verification, find <strong>"leadgen"</strong> in the subscription fields and <strong>subscribe</strong> to it.
            </li>
          </ol>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-blue-800">
            <strong>Important:</strong> Your server must be publicly accessible (deployed) for Meta to reach the webhook URL. This won't work on localhost.
          </div>
        </div>
      </Step>

      {/* Step 5 */}
      <Step number={5} title="Request Permissions (App Review)" icon={Shield}>
        <div className="space-y-3 text-sm text-slate-600 mt-3">
          <p>
            To access leads from pages you don't own (or to go fully live), you need Meta's App Review.
            For your own pages in Development mode, admin accounts can test immediately.
          </p>
          <ol className="list-decimal list-inside space-y-2">
            <li>Go to <strong>App Review &rarr; Permissions and Features</strong>.</li>
            <li>
              Request these permissions:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1 text-slate-500">
                <li><code className="bg-slate-100 px-1 rounded">ads_read</code> — Read ad campaigns</li>
                <li><code className="bg-slate-100 px-1 rounded">leads_retrieval</code> — Download lead form data</li>
                <li><code className="bg-slate-100 px-1 rounded">pages_read_engagement</code> — Read page content</li>
                <li><code className="bg-slate-100 px-1 rounded">pages_manage_ads</code> — Manage page ads</li>
              </ul>
            </li>
            <li>Submit a screencast showing your app's use case. Meta usually reviews within 1-5 business days.</li>
          </ol>
          <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-green-800">
            <strong>Quick test:</strong> You can skip App Review initially if you only need leads from pages where you are an admin. Dev mode gives you immediate access.
          </div>
        </div>
      </Step>

      {/* Step 6 */}
      <Step number={6} title="Subscribe Your Page to the App" icon={Settings}>
        <div className="space-y-3 text-sm text-slate-600 mt-3">
          <ol className="list-decimal list-inside space-y-2">
            <li>
              Go to{' '}
              <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-blue-600 underline font-medium">
                Graph API Explorer
              </a>.
            </li>
            <li>Select your app from the dropdown. Generate a <strong>Page Access Token</strong> for the page running ads.</li>
            <li>
              Make a POST request to subscribe the page to your app's webhooks:
            </li>
          </ol>
          <div className="bg-slate-900 rounded-lg p-4 text-green-400 font-mono text-xs leading-relaxed">
            <div>POST /<span className="text-yellow-300">{'<PAGE_ID>'}</span>/subscribed_apps</div>
            <div className="text-slate-500 mt-1"># With page access token</div>
            <div>subscribed_fields=leadgen</div>
          </div>
          <p>This tells Meta to send real-time lead notifications to your webhook when a lead form is submitted on that page.</p>
        </div>
      </Step>

      {/* Final Env Summary */}
      <div className="bg-slate-900 rounded-xl p-6 text-sm">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2">
          <Key size={16} className="text-yellow-400" />
          Complete .env Configuration
        </h3>
        <div className="font-mono text-xs leading-relaxed space-y-1">
          <div><span className="text-slate-500"># Meta Ads Integration</span></div>
          <div className="text-green-400">META_APP_ID=<span className="text-yellow-300">your_app_id</span></div>
          <div className="text-green-400">META_APP_SECRET=<span className="text-yellow-300">your_app_secret</span></div>
          <div className="text-green-400">META_CALLBACK_URL=<span className="text-yellow-300">{callbackUrl}</span></div>
          <div className="text-green-400">META_WEBHOOK_VERIFY_TOKEN=<span className="text-yellow-300">your_chosen_verify_token</span></div>
        </div>
      </div>

      {/* Connect CTA */}
      <div className="bg-white rounded-xl border p-6 text-center">
        <CheckCircle2 size={32} className="text-green-500 mx-auto mb-3" />
        <h3 className="font-bold text-slate-900 mb-1">All set?</h3>
        <p className="text-sm text-slate-500 mb-4">
          Once you've added the environment variables and restarted the server, go back to the Dashboard and click <strong>"Connect with Facebook"</strong>.
        </p>
        <Button
          onClick={onBack}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6"
          data-testid="guide-go-to-dashboard-btn"
        >
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
}
